import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/supabaseService';
import toast from 'react-hot-toast';

const MotionCard = motion(Card);

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  
  // Personal information state
  const [age, setAge] = useState<number | null>(null);
  const [annualIncome, setAnnualIncome] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load personal information from user profile and localStorage (fallback)
  useEffect(() => {
    if (user) {
      // First try to load from user profile
      if (user.age) {
        setAge(user.age);
      } else {
        // Fallback to localStorage
        const savedAge = localStorage.getItem('userAge');
        if (savedAge) {
          const ageValue = parseInt(savedAge);
          if (ageValue >= 18 && ageValue <= 100) {
            setAge(ageValue);
          }
        }
      }

      if (user.annual_income) {
        setAnnualIncome(user.annual_income);
      } else {
        // Fallback to localStorage
        const savedIncome = localStorage.getItem('currentIncome');
        if (savedIncome) {
          const incomeValue = parseFloat(savedIncome);
          if (incomeValue > 0) {
            setAnnualIncome(incomeValue);
          }
        }
      }
    }
  }, [user]);

  const handleSavePersonalInfo = async () => {
    if (age && (age < 18 || age > 100)) {
      toast.error('Please enter a valid age between 18 and 100');
      return;
    }
    
    if (annualIncome && annualIncome <= 0) {
      toast.error('Please enter a valid annual income');
      return;
    }

    if (!user?.id) {
      toast.error('User not found');
      return;
    }

    try {
      // Save to database
      const updates: any = {};
      if (age !== null) {
        updates.age = age;
      }
      if (annualIncome !== null) {
        updates.annual_income = annualIncome;
      }

      if (Object.keys(updates).length > 0) {
        await userService.updateUser(user.id, updates);
        
        // Update the user state in AuthContext
        updateUser(updates);
        
        // Also save to localStorage for backward compatibility
        if (age) {
          localStorage.setItem('userAge', age.toString());
        }
        if (annualIncome) {
          localStorage.setItem('currentIncome', annualIncome.toString());
        }
      }

      setIsEditing(false);
      toast.success('Personal information saved successfully!');
    } catch (error) {
      console.error('Error saving personal information:', error);
      toast.error('Failed to save personal information');
    }
  };

  const handleUpgradeClick = () => {
    console.log('🔄 Upgrade button clicked, navigating to pricing page');
    navigate('/pricing');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" sx={{ mb: 4, fontWeight: 'bold' }}>
        Profile Settings
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'primary.main',
                    color: 'black',
                    fontSize: '2rem',
                    mr: 3,
                  }}
                >
                  {user?.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {user?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={isPremium ? 'Premium' : 'Free'}
                      color={isPremium ? 'primary' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
              <Button variant="outlined" fullWidth>
                Edit Profile
              </Button>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Subscription
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isPremium
                  ? 'You have access to all premium features.'
                  : 'Upgrade to unlock unlimited goals and advanced features.'}
              </Typography>
              {!isPremium && (
                <Button variant="contained" fullWidth onClick={handleUpgradeClick}>
                  Upgrade to Premium
                </Button>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Personal Information
                </Typography>
                <Button
                  variant={isEditing ? "contained" : "outlined"}
                  onClick={() => isEditing ? handleSavePersonalInfo() : setIsEditing(true)}
                >
                  {isEditing ? 'Save' : 'Edit'}
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This information is used to calculate your personalized healthworth and impact metrics.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Age"
                    type="number"
                    fullWidth
                    value={age || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setAge(null);
                      } else {
                        const ageValue = parseInt(value);
                        if (!isNaN(ageValue)) {
                          setAge(ageValue);
                        }
                      }
                    }}
                    inputProps={{ min: 18, max: 100 }}
                    disabled={!isEditing}
                    helperText={isEditing ? "Used to calculate years until retirement (67)" : age ? `${age} years old` : "Not set"}
                    error={isEditing && age !== null && (age < 18 || age > 100)}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Annual Income"
                    type="number"
                    fullWidth
                    value={annualIncome || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setAnnualIncome(null);
                      } else {
                        const incomeValue = parseFloat(value);
                        if (!isNaN(incomeValue)) {
                          setAnnualIncome(incomeValue);
                        }
                      }
                    }}
                    inputProps={{ min: 0, step: 1000 }}
                    disabled={!isEditing}
                    helperText={isEditing ? "Projected with 2% annual growth to retirement" : annualIncome ? `$${annualIncome.toLocaleString()} per year` : "Not set"}
                    error={isEditing && annualIncome !== null && annualIncome <= 0}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile; 